import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, RefreshCw, CheckCircle2, XCircle, Sparkles, ArrowUpCircle,
  Pause, Play, Clock, Infinity as InfinityIcon, DollarSign, Server, Edit,
} from "lucide-react";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge } from "../../components/dashboard/Shared";

const PLAN_OPTIONS = [
  { v: "STARTER", l: "Starter (1 MT5)" },
  { v: "PROFESSIONAL", l: "Professional (5 MT5)" },
  { v: "BUSINESS", l: "Business (10 MT5)" },
  { v: "ENTERPRISE", l: "Enterprise (Unlimited)" },
];
const STATUS_OPTIONS = ["active", "inactive", "suspended", "expired", "lifetime"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

export default function Subscriptions() {
  const [rows, setRows] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [tab, setTab] = useState("subscribers");
  const [modalUser, setModalUser] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/subscriptions", { params: { limit: 500 } }),
      api.get("/admin/subscriptions/requests/all"),
    ])
      .then(([s, r]) => {
        setRows(s.data.rows || []);
        setReqs(r.data.requests || []);
      })
      .catch(() => toast.error("Failed to load subscriptions"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (planFilter && r.plan_code !== planFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(r.email || "").toLowerCase().includes(q) && !(r.name || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, planFilter, search]);

  const pendingReqs = reqs.filter((r) => r.status === "pending");

  const resolve = async (requestId, action) => {
    try {
      const { data } = await api.post(`/admin/subscriptions/requests/${requestId}/resolve`, { action });
      if (action !== "approved") toast.success("Rejected");
      else if (data?.desired_plan) toast.success(`Approved — ${data.desired_plan} plan activated`);
      else toast.success("Approved — now set the plan via Manage (no plan was requested)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update request");
    }
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Subscription Management" sub="Manage plans, expiries and upgrade requests. Payments are collected manually.">
        <button onClick={load} className="btn-outline flex items-center gap-2 text-sm" data-testid="sub-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </PageHeader>

      {/* Tab strip */}
      <div className="flex gap-1 border-b border-white/10">
        <button
          data-testid="tab-subscribers"
          onClick={() => setTab("subscribers")}
          className={`px-4 py-2 text-sm ${tab === "subscribers" ? "text-white border-b-2 border-[#7000FF]" : "text-gray-400"}`}>
          Subscribers ({rows.length})
        </button>
        <button
          data-testid="tab-requests"
          onClick={() => setTab("requests")}
          className={`px-4 py-2 text-sm relative ${tab === "requests" ? "text-white border-b-2 border-[#7000FF]" : "text-gray-400"}`}>
          Upgrade Requests
          {pendingReqs.length > 0 && (
            <span className="ml-2 text-[10px] bg-[#FF3B30] text-white rounded-full px-1.5 py-0.5">{pendingReqs.length}</span>
          )}
        </button>
      </div>

      {tab === "subscribers" && (
        <>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                data-testid="sub-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email or name…"
                className="input-dark !pl-9 !w-64"
              />
            </div>
            <select data-testid="sub-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="input-dark !w-44">
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="sub-plan-filter" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                    className="input-dark !w-52">
              <option value="">All plans</option>
              {PLAN_OPTIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>

          <div className="card-dark overflow-x-auto" data-testid="subscribers-table">
            {loading ? (
              <div className="p-8 skeleton h-40" />
            ) : filtered.length === 0 ? (
              <p className="p-8 text-center text-gray-500 text-sm">No subscribers match the filters.</p>
            ) : (
              <Table headers={["User", "Plan", "Status", "MT5 Used / Limit", "Expiry", "Provider", ""]}>
                {filtered.map((r) => (
                  <tr key={r.user_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <Td>
                      <div>
                        <p className="text-white text-sm">{r.name || "—"}</p>
                        <p className="text-xs text-gray-500">{r.email}</p>
                      </div>
                    </Td>
                    <Td className="text-[#00F0FF]">{r.plan_code || "—"}</Td>
                    <Td><StatusBadge status={r.status} /></Td>
                    <Td className="tabular">
                      <span className={r.mt5_limit >= 0 && r.mt5_used >= r.mt5_limit ? "text-[#FF3B30]" : "text-white"}>
                        {r.mt5_used}
                      </span>
                      <span className="text-gray-500"> / {r.mt5_limit_display}</span>
                      {r.mt5_override !== null && r.mt5_override !== undefined && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#7000FF]/20 text-[#B794F4]">OVR</span>
                      )}
                    </Td>
                    <Td className="text-xs">{fmtDate(r.expiry_date)}</Td>
                    <Td className="text-xs uppercase tracking-wider text-gray-500">{r.provider || "manual"}</Td>
                    <Td>
                      <button
                        onClick={() => setModalUser(r)}
                        data-testid={`manage-btn-${r.user_id}`}
                        className="text-[#00F0FF] hover:text-white text-xs flex items-center gap-1">
                        <Edit size={12} /> Manage
                      </button>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </>
      )}

      {tab === "requests" && (
        <div className="card-dark overflow-x-auto" data-testid="upgrade-requests-table">
          {reqs.length === 0 ? (
            <p className="p-8 text-center text-gray-500 text-sm">No upgrade requests yet.</p>
          ) : (
            <Table headers={["Received", "Request ID", "User", "Requested Plan", "Declared Equity", "Message", "Status", ""]}>
              {reqs.map((r) => (
                <tr key={r.request_id} className="border-b border-white/5" data-testid={`req-row-${r.request_id}`}>
                  <Td className="text-xs">{fmtDate(r.created_at)}</Td>
                  <Td className="text-[11px] font-mono text-gray-400" data-testid={`req-id-${r.request_id}`}>{r.request_id}</Td>
                  <Td>
                    <p className="text-white text-sm">{r.user_name}</p>
                    <p className="text-xs text-gray-500">{r.user_email}</p>
                  </Td>
                  <Td className="text-[#00F0FF]">{r.desired_plan || "—"}</Td>
                  <Td className="tabular text-xs">{r.declared_equity != null ? `$${Number(r.declared_equity).toLocaleString()}` : "—"}</Td>
                  <Td className="text-xs max-w-md truncate" title={r.message}>{r.message || "—"}</Td>
                  <Td><StatusBadge status={r.status === "pending" ? "pending" : r.status === "approved" ? "approved" : "rejected"} /></Td>
                  <Td>
                    {r.status === "pending" ? (
                      <div className="flex gap-2">
                        <button
                          data-testid={`req-approve-${r.request_id}`}
                          onClick={() => resolve(r.request_id, "approved")}
                          className="text-[#00E676] hover:text-white text-xs flex items-center gap-1">
                          <CheckCircle2 size={12} /> Approve
                        </button>
                        <button
                          data-testid={`req-reject-${r.request_id}`}
                          onClick={() => resolve(r.request_id, "rejected")}
                          className="text-[#FF3B30] hover:text-white text-xs flex items-center gap-1">
                          <XCircle size={12} /> Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">{fmtDate(r.resolved_at)}</span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      )}

      {modalUser && (
        <ManageModal
          row={modalUser}
          onClose={() => setModalUser(null)}
          onSaved={() => { setModalUser(null); load(); }}
        />
      )}
    </div>
  );
}

/** Full-featured admin management modal for a single subscription. */
function ManageModal({ row, onClose, onSaved }) {
  const [plan, setPlan] = useState(row.plan_code || "STARTER");
  const [status, setStatus] = useState(row.status || "inactive");
  const [expiry, setExpiry] = useState(row.expiry_date ? row.expiry_date.slice(0, 10) : "");
  const [customPrice, setCustomPrice] = useState(row.custom_price ?? "");
  const [override, setOverride] = useState(row.mt5_override ?? "");
  const [notes, setNotes] = useState(row.payment_notes || "");
  const [renewDays, setRenewDays] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const call = async (path, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/subscriptions/${row.user_id}${path}`, body);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="manage-subscription-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative card-dark w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto border border-white/10">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-white text-lg">{row.name || row.email}</h3>
            <p className="text-xs text-gray-500">{row.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">×</button>
        </div>

        <div className="space-y-5">
          {/* Change Plan */}
          <Section title="Change Plan" icon={ArrowUpCircle}>
            <div className="grid grid-cols-3 gap-2">
              <select value={plan} onChange={(e) => setPlan(e.target.value)} className="input-dark" data-testid="modal-plan-select">
                {PLAN_OPTIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
              <input type="number" min={0} value={renewDays} onChange={(e) => setRenewDays(parseInt(e.target.value) || 0)}
                     className="input-dark" placeholder="Days" data-testid="modal-days-input" />
              <button
                data-testid="modal-change-plan-btn"
                disabled={busy}
                onClick={() => call("/change-plan", { plan_code: plan, extend_days: renewDays, activate: true, note })}
                className="btn-primary py-2 text-sm">Apply</button>
            </div>
          </Section>

          {/* Status */}
          <Section title="Subscription Status" icon={Sparkles}>
            <div className="grid grid-cols-3 gap-2">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-dark" data-testid="modal-status-select">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                data-testid="modal-set-status-btn"
                disabled={busy} onClick={() => call("/status", { status, note })}
                className="btn-outline py-2 text-sm">Set Status</button>
              <div className="flex gap-1">
                <button disabled={busy} data-testid="modal-suspend-btn"
                        onClick={() => call("/status", { status: "suspended" })}
                        className="btn-outline flex-1 py-2 text-xs text-[#FF3B30]"><Pause size={12} className="inline" /> Suspend</button>
                <button disabled={busy} data-testid="modal-activate-btn"
                        onClick={() => call("/status", { status: "active" })}
                        className="btn-outline flex-1 py-2 text-xs text-[#00E676]"><Play size={12} className="inline" /> Activate</button>
              </div>
            </div>
          </Section>

          {/* Renew */}
          <Section title="Renew Subscription" icon={Clock}>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" min={1} value={renewDays} onChange={(e) => setRenewDays(parseInt(e.target.value) || 1)}
                     className="input-dark" placeholder="Days to extend" data-testid="modal-renew-days" />
              <button disabled={busy} data-testid="modal-renew-btn"
                      onClick={() => call("/renew", { days: renewDays, note })}
                      className="btn-primary py-2 text-sm">Renew +{renewDays}d</button>
              <button disabled={busy} data-testid="modal-expire-btn"
                      onClick={() => call("/status", { status: "expired" })}
                      className="btn-outline py-2 text-sm text-yellow-400">Expire Now</button>
            </div>
          </Section>

          {/* Custom expiry */}
          <Section title="Custom Expiry Date" icon={Clock}>
            <div className="grid grid-cols-3 gap-2">
              <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                     className="input-dark" data-testid="modal-expiry-input" />
              <button disabled={busy} data-testid="modal-set-expiry-btn"
                      onClick={() => call("/expiry", { expiry_date: expiry ? new Date(expiry).toISOString() : null })}
                      className="btn-outline py-2 text-sm">Set Expiry</button>
              <button disabled={busy} data-testid="modal-clear-expiry-btn"
                      onClick={() => { setExpiry(""); call("/expiry", { expiry_date: null }); }}
                      className="btn-outline py-2 text-sm text-gray-400">Clear</button>
            </div>
          </Section>

          {/* Custom Price */}
          <Section title="Custom Price" icon={DollarSign}>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" min={0} step={100} value={customPrice} onChange={(e) => setCustomPrice(e.target.value)}
                     className="input-dark" placeholder="Custom monthly ₹" data-testid="modal-price-input" />
              <button disabled={busy} data-testid="modal-set-price-btn"
                      onClick={() => call("/price", { custom_price: customPrice ? parseFloat(customPrice) : null })}
                      className="btn-outline py-2 text-sm">Set Price</button>
              <button disabled={busy} data-testid="modal-clear-price-btn"
                      onClick={() => { setCustomPrice(""); call("/price", { custom_price: null }); }}
                      className="btn-outline py-2 text-sm text-gray-400">Clear</button>
            </div>
          </Section>

          {/* Lifetime */}
          <Section title="Grant Lifetime Access" icon={InfinityIcon}>
            <div className="grid grid-cols-3 gap-2">
              <select value={plan} onChange={(e) => setPlan(e.target.value)} className="input-dark" data-testid="modal-lifetime-plan">
                {PLAN_OPTIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
              <button disabled={busy} data-testid="modal-grant-lifetime-btn"
                      onClick={() => call("/lifetime", { plan_code: plan, note })}
                      className="btn-primary py-2 text-sm">Grant Lifetime</button>
              <span className="text-xs text-gray-500 flex items-center">No expiry, no auto-renewal.</span>
            </div>
          </Section>

          {/* MT5 override */}
          <Section title="MT5 Limit Override" icon={Server}>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" min={-1} value={override} onChange={(e) => setOverride(e.target.value)}
                     className="input-dark" placeholder="e.g. 3 (or -1 = unlimited)" data-testid="modal-override-input" />
              <button disabled={busy} data-testid="modal-set-override-btn"
                      onClick={() => call("/mt5-override", { override: override === "" ? null : parseInt(override), note })}
                      className="btn-outline py-2 text-sm">Set Override</button>
              <button disabled={busy} data-testid="modal-clear-override-btn"
                      onClick={() => { setOverride(""); call("/mt5-override", { override: null }); }}
                      className="btn-outline py-2 text-sm text-gray-400">Clear</button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Overrides the plan's default MT5 limit for this user only. -1 means unlimited.</p>
          </Section>

          {/* Payment Notes */}
          <Section title="Payment Notes" icon={Edit}>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={3} className="input-dark resize-none" placeholder="Internal notes about how this user pays…"
                      data-testid="modal-notes-input" />
            <button disabled={busy} data-testid="modal-save-notes-btn"
                    onClick={() => call("/notes", { payment_notes: notes })}
                    className="btn-outline py-2 text-sm mt-2">Save Notes</button>
          </Section>

          <div>
            <label className="label-xs">Change note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)}
                   className="input-dark mt-1" placeholder="Reason logged in history"
                   data-testid="modal-change-note" />
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="border border-white/5 rounded-md p-3">
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#7000FF]" />}
      <p className="text-xs text-gray-300 uppercase tracking-wider">{title}</p>
    </div>
    {children}
  </div>
);
